import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fieldKey } from '../fieldKeys';
import { Button, Dialog, IconButton, Select, TextField, useConfirm } from '../ui';
import { EditIcon, PlusIcon, TrashIcon } from '../ui/icons';

/**
 * The paths a sample API answer offers (at most four levels deep): each with
 * its type and what it is (`kind`: array, arrayField, field, nested array
 * field), for the mapping builder's lists.
 */
function analyzeSampleResponse(data, prefix = '', maxDepth = 4, currentDepth = 0) {
  const paths = [];
  if (currentDepth >= maxDepth) return paths;

  if (Array.isArray(data)) {
    if (prefix) paths.push({ path: prefix, type: 'array', kind: 'array', count: data.length });
    const first = data[0];
    if (first && typeof first === 'object') {
      Object.keys(first).forEach(key => {
        const currentPath = prefix ? `${prefix}[].${key}` : `${key}`;
        const value = first[key];
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          paths.push(...analyzeSampleResponse(value, currentPath, maxDepth, currentDepth + 1));
        } else {
          paths.push({ path: currentPath, type: Array.isArray(value) ? 'array' : typeof value, kind: 'arrayField' });
        }
      });
    }
  } else if (data && typeof data === 'object') {
    Object.keys(data).forEach(key => {
      const currentPath = prefix ? `${prefix}.${key}` : key;
      const value = data[key];
      if (Array.isArray(value)) {
        paths.push({ path: currentPath, type: 'array', kind: 'array', count: value.length });
        if (value.length > 0 && typeof value[0] === 'object') {
          Object.keys(value[0]).forEach(subKey => {
            paths.push({ path: `${currentPath}[].${subKey}`, type: typeof value[0][subKey], kind: 'nested', array: key });
          });
        }
      } else if (value && typeof value === 'object') {
        paths.push(...analyzeSampleResponse(value, currentPath, maxDepth, currentDepth + 1));
      } else {
        paths.push({ path: currentPath, type: typeof value, kind: 'field' });
      }
    });
  }
  return paths;
}

const extractValueFromPath = (data, path) => {
  if (!path) return data;
  let current = data;
  for (const part of path.split('.')) {
    if (current && typeof current === 'object' && current[part] !== undefined) current = current[part];
    else return null;
  }
  return current;
};

/** A mapping expression from the builder's choices, or '' while incomplete. */
const expressionOf = ({ mappingType, simplePath, arrayPath, filterField, filterValue, targetField }) => {
  if (mappingType === 'simple') return simplePath;
  if (mappingType === 'filtered') {
    return arrayPath && filterField && filterValue && targetField ? `${arrayPath}[${filterField}=${filterValue}].${targetField}` : '';
  }
  if (mappingType === 'all') return arrayPath && targetField ? `${arrayPath}[].${targetField}` : '';
  return '';
};

const EMPTY_BUILDER = { mappingType: 'simple', simplePath: '', arrayPath: '', filterField: '', filterValue: '', targetField: '' };

/**
 * Field ← path of the API's answer, for a completion agent (deprecated):
 * a simple path, an array's items filtered on a value (crew[job=Director].name),
 * or every item of an array (cast[].name). Controlled: `fieldMappings` in,
 * `onMappingsChange(next)` out.
 */
function FieldMappingBuilder({ fieldMappings = {}, onMappingsChange, sampleResponse = null, inventoryFields = [] }) {
  const { t } = useTranslation();
  const mappings = fieldMappings || {};
  const availablePaths = useMemo(() => (sampleResponse ? analyzeSampleResponse(sampleResponse) : []), [sampleResponse]);
  const confirm = useConfirm();
  const [editingField, setEditingField] = useState(null);   // null: the builder is closed
  // The mapping being edited (its field), or null when one is added: kept apart from the field chosen.
  const [original, setOriginal] = useState(null);
  const [builder, setBuilder] = useState(EMPTY_BUILDER);
  const set = (patch) => setBuilder(prev => ({ ...prev, ...patch }));

  // Mapping targets are field keys; people see the field's name.
  const fieldLabel = (key) => {
    const field = inventoryFields.find(f => fieldKey(f) === key);
    return field ? field.name : key;
  };
  const describe = (path) => (path.kind === 'array' ? t('mappingBuilder.kinds.array', { count: path.count })
    : path.kind === 'nested' ? t('mappingBuilder.kinds.nested', { name: path.array })
      : t(`mappingBuilder.kinds.${path.kind}`));

  const addMapping = () => {
    setBuilder(EMPTY_BUILDER);
    setOriginal(null);
    setEditingField('');
  };

  const editMapping = (field) => {
    const current = mappings[field] || '';
    let next = { ...EMPTY_BUILDER, simplePath: current };
    // Parse the existing expression to fill the form.
    if (current.includes('[') && current.includes(']')) {
      const filtered = current.match(/^(.+?)\[(.+?)=(.+?)\]\.(.+)$/);
      const all = current.match(/^(.+?)\[\]\.(.+)$/);
      if (current.includes('=') && filtered) {
        next = { ...EMPTY_BUILDER, mappingType: 'filtered', arrayPath: filtered[1], filterField: filtered[2], filterValue: filtered[3], targetField: filtered[4] };
      } else if (all) {
        next = { ...EMPTY_BUILDER, mappingType: 'all', arrayPath: all[1], targetField: all[2] };
      }
    }
    setBuilder(next);
    setOriginal(field);
    setEditingField(field);
  };

  const close = () => setEditingField(null);
  const expression = expressionOf(builder);

  // Another field than the one edited, already mapped: replacing its mapping asks first.
  const taken = (key) => key !== original && Object.prototype.hasOwnProperty.call(mappings, key);
  const saveMapping = async () => {
    if (!editingField || !expression) return;
    if (taken(editingField) && !(await confirm({
      title: t('mappingBuilder.confirmReplaceTitle'),
      message: t('mappingBuilder.confirmReplace', { name: fieldLabel(editingField), expression: mappings[editingField] }),
      confirmLabel: t('mappingBuilder.replace'),
      danger: true,
    }))) return;
    const next = { ...mappings };
    // Editing a mapping onto another field moves it there.
    if (original && original !== editingField) delete next[original];
    next[editingField] = expression;
    onMappingsChange(next);
    close();
  };

  const deleteMapping = (field) => {
    const next = { ...mappings };
    delete next[field];
    onMappingsChange(next);
  };

  const arrayPaths = availablePaths.filter(p => p.type === 'array').map(p => p.path);
  const fieldsForArray = (arrayPath) => {
    const prefix = `${arrayPath}[].`;
    return availablePaths.filter(p => p.path.startsWith(prefix)).map(p => p.path.substring(prefix.length));
  };
  const filterValuesFor = (arrayPath, filterField) => {
    if (!sampleResponse || !arrayPath || !filterField) return [];
    const arrayData = extractValueFromPath(sampleResponse, arrayPath);
    if (!Array.isArray(arrayData)) return [];
    const values = new Set();
    arrayData.forEach(item => {
      if (item && typeof item === 'object' && item[filterField] !== undefined) values.add(item[filterField]);
    });
    return Array.from(values).sort();
  };
  const filterValues = filterValuesFor(builder.arrayPath, builder.filterField);

  return (
    <section className="mapping-builder" aria-labelledby="mapping-builder-title">
      <div className="settings-card__head">
        <h3 id="mapping-builder-title" className="settings-card__title">{t('mappingBuilder.title')}</h3>
        <Button variant="secondary" size="sm" icon={PlusIcon} onClick={addMapping}>{t('mappingBuilder.add')}</Button>
      </div>

      {Object.keys(mappings).length === 0 ? (
        <p className="settings-muted">{t('mappingBuilder.none')}</p>
      ) : (
        <ul className="field-list">
          {Object.entries(mappings).map(([field, mapping]) => (
            <li key={field} className="field-row">
              <div className="field-row__main">
                <p className="field-row__name">
                  <strong>{fieldLabel(field)}</strong>
                  {fieldLabel(field) !== field && <span className="field-row__type">{field}</span>}
                </p>
                <code className="settings-code">{mapping}</code>
              </div>
              <div className="field-row__actions">
                <IconButton icon={EditIcon} size="sm" variant="ghost" label={t('mappingBuilder.editNamed', { name: fieldLabel(field) })}
                            onClick={() => editMapping(field)} />
                <IconButton icon={TrashIcon} size="sm" variant="ghost" className="field-row__remove"
                            label={t('mappingBuilder.deleteNamed', { name: fieldLabel(field) })} onClick={() => deleteMapping(field)} />
              </div>
            </li>
          ))}
        </ul>
      )}

      <Dialog
        open={editingField !== null}
        onClose={close}
        title={original ? t('mappingBuilder.editTitle', { name: fieldLabel(original) }) : t('mappingBuilder.addTitle')}
        size="lg"
        footer={(
          <>
            <Button variant="secondary" onClick={close}>{t('common.cancel')}</Button>
            <Button onClick={saveMapping} disabled={!editingField || !expression}>{t('mappingBuilder.save')}</Button>
          </>
        )}
      >
        <div className="dialog-form">
          <Select label={t('mappingBuilder.field')} value={editingField || ''} onChange={(e) => setEditingField(e.target.value)}
                  hint={inventoryFields.length === 0 ? t('mappingBuilder.noFields') : undefined}>
            <option value="">{t('mappingBuilder.chooseField')}</option>
            {inventoryFields.map((field) => (
              <option key={fieldKey(field)} value={fieldKey(field)}>
                {field.name} ({field.type}){taken(fieldKey(field)) ? ` — ${t('mappingBuilder.alreadyMapped')}` : ''}
              </option>
            ))}
          </Select>
          <Select label={t('mappingBuilder.type')} value={builder.mappingType} onChange={(e) => set({ mappingType: e.target.value })}>
            <option value="simple">{t('mappingBuilder.types.simple')}</option>
            <option value="filtered">{t('mappingBuilder.types.filtered')}</option>
            <option value="all">{t('mappingBuilder.types.all')}</option>
          </Select>

          {builder.mappingType === 'simple' && (
            <>
              <TextField label={t('mappingBuilder.path')} value={builder.simplePath} onChange={(e) => set({ simplePath: e.target.value })}
                         placeholder="title, release_date, overview" />
              {availablePaths.length > 0 && (
                <div className="mapping-builder__paths">
                  <p className="ui-field__label">{t('mappingBuilder.available')}</p>
                  <ul>
                    {availablePaths.filter(p => p.type !== 'array').map((path) => (
                      <li key={path.path}>
                        <button type="button" className="mapping-builder__path" onClick={() => set({ simplePath: path.path })}>
                          <code>{path.path}</code> <span className="settings-muted">{describe(path)}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {(builder.mappingType === 'filtered' || builder.mappingType === 'all') && (
            <>
              <Select label={t('mappingBuilder.arrayPath')} value={builder.arrayPath} onChange={(e) => set({ arrayPath: e.target.value })}>
                <option value="">{t('mappingBuilder.chooseArray')}</option>
                {arrayPaths.map((path) => <option key={path} value={path}>{path}</option>)}
              </Select>
              {builder.mappingType === 'filtered' && (
                <>
                  <Select label={t('mappingBuilder.filterField')} value={builder.filterField}
                          hint={!builder.arrayPath ? t('mappingBuilder.arrayFirst') : undefined}
                          onChange={(e) => set({ filterField: e.target.value, filterValue: '' })}>
                    <option value="">{t('mappingBuilder.chooseFilterField')}</option>
                    {builder.arrayPath && fieldsForArray(builder.arrayPath).map((field) => <option key={field} value={field}>{field}</option>)}
                  </Select>
                  <Select label={t('mappingBuilder.filterValue')} value={builder.filterValue} disabled={!builder.filterField}
                          hint={!builder.filterField ? t('mappingBuilder.filterFieldFirst') : undefined}
                          error={builder.filterField && filterValues.length === 0 ? t('mappingBuilder.noValues') : undefined}
                          onChange={(e) => set({ filterValue: e.target.value })}>
                    <option value="">{t('mappingBuilder.chooseFilterValue')}</option>
                    {filterValues.map((value) => <option key={String(value)} value={value}>{String(value)}</option>)}
                  </Select>
                </>
              )}
              <Select label={t('mappingBuilder.target')} value={builder.targetField} onChange={(e) => set({ targetField: e.target.value })}>
                <option value="">{t('mappingBuilder.chooseTarget')}</option>
                {builder.arrayPath && fieldsForArray(builder.arrayPath).map((field) => <option key={field} value={field}>{field}</option>)}
              </Select>
            </>
          )}

          <div className="dialog-preview">
            <p className="dialog-preview__title">{t('mappingBuilder.preview')}</p>
            <code>{expression || t('mappingBuilder.incomplete')}</code>
          </div>
        </div>
      </Dialog>
    </section>
  );
}

export default FieldMappingBuilder;
